import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { faArrowUpRight, faTrophy } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { classNames } from "@/utils/classNames";

import { useGetTopCampaignsQuery } from "../../../redux/analytics/analytics.api";

import { AnalyticsEmptyState } from "../components/AnalyticsEmptyState";
import { AnalyticsSkeleton } from "../components/AnalyticsSkeleton";
import { AnalyticsSelection } from "../hooks/useAnalyticsSelection";
import { useOpenCampaignDeepDive } from "../hooks/useOpenCampaignDeepDive";
import { formatNumber } from "../utils/percentage";

export interface TopCampaignsTabProps {
	selection: AnalyticsSelection;
}

const formatRate = (value: number | null): string =>
	value === null ? "—" : `${value.toFixed(1)}%`;

const CELL = "px-4 py-3 text-right tabular-nums whitespace-nowrap";

/**
 * The company's best campaigns, all time.
 *
 * Deliberately ignores the page's list / date selection — the question is "what has
 * worked for us", not "how is this selection doing", which is what the other tabs answer.
 */
export const TopCampaignsTab: FunctionComponent<TopCampaignsTabProps> = ({ selection }) => {
	const { t } = useTranslation();
	const { data, isLoading } = useGetTopCampaignsQuery({});

	/** Same click-through as the overview: find the campaign, then look at it. */
	const openCampaign = useOpenCampaignDeepDive(selection.selectListsWithParams);

	if (isLoading) {
		return <AnalyticsSkeleton />;
	}

	if (!data) {
		return <AnalyticsEmptyState />;
	}

	return (
		<div className="flex flex-col w-full mt-4 max-w-5xl">
			<div className="rounded-xl border border-border-light bg-brand-white overflow-hidden">
				<div className="flex flex-row items-center gap-3 px-4 py-3 border-b border-border-light">
					<div className="flex items-center justify-center w-8 h-8 rounded-lg bg-status-success-light text-status-success">
						<Icon className="w-4 h-4" icon={faTrophy} />
					</div>
					<div className="text-sm font-semibold text-text">{t("analytics.top.title")}</div>
				</div>

				<div className="overflow-x-auto">
					<table className="w-full text-sm">
						<thead>
							<tr className="bg-background-light">
								<th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-text-meta">
									{t("analytics.top.columns.campaign")}
								</th>
								<th className="px-4 py-2 text-right text-xs font-semibold uppercase tracking-wide text-text-meta">
									{t("analytics.top.columns.assigned")}
								</th>
								<th className="px-4 py-2 text-right text-xs font-semibold uppercase tracking-wide text-text-meta">
									{t("analytics.top.columns.contacted")}
								</th>
								<th className="px-4 py-2 text-right text-xs font-semibold uppercase tracking-wide text-text-meta">
									{t("analytics.top.columns.replyRate")}
								</th>
								<th className="px-4 py-2 text-right text-xs font-semibold uppercase tracking-wide text-text-meta">
									{t("analytics.top.columns.positiveRate")}
								</th>
								<th className="px-4 py-2 text-right text-xs font-semibold uppercase tracking-wide text-text-meta">
									{t("analytics.top.columns.sentiment")}
								</th>
							</tr>
						</thead>
						<tbody>
							<tr className="bg-background-light/60 border-b border-border-light font-semibold">
								<td className="px-4 py-3 text-left text-text">{t("analytics.top.lifetimeRow")}</td>
								<td className={CELL}>{formatNumber(data.lifetime.assignedLeads)}</td>
								<td className={CELL}>{formatNumber(data.lifetime.contactedLeads)}</td>
								<td className={CELL}>{formatRate(data.lifetime.replyRate)}</td>
								<td className={classNames(CELL, "text-status-success")}>
									{formatRate(data.lifetime.positiveRate)}
								</td>
								<td className={CELL}>{formatRate(data.lifetime.sentimentRatio)}</td>
							</tr>

							{data.campaigns.map((campaign, index) => (
								<tr
									key={`${campaign.listId}-${campaign.campaignId}`}
									className="border-b border-border-light last:border-b-0 hover:bg-background-light"
								>
									<td className="px-4 py-3 text-left">
										<button
											className="group flex flex-row items-center gap-2 cursor-pointer text-left"
											type="button"
											onClick={() => openCampaign(campaign)}
										>
											<span className="flex items-center justify-center w-5 h-5 rounded-md bg-blue-action-200 text-[11px] font-bold text-brand-light shrink-0">
												{index + 1}
											</span>
											<span className="text-text">{campaign.campaignName}</span>
											<span className="text-xs text-text-meta">· {campaign.listName}</span>
											<Icon
												className="w-2.5 h-2.5 opacity-0 group-hover:opacity-100 text-brand-light"
												icon={faArrowUpRight}
											/>
										</button>
									</td>
									<td className={CELL}>{formatNumber(campaign.assignedLeads)}</td>
									<td className={CELL}>{formatNumber(campaign.contactedLeads)}</td>
									<td className={CELL}>{formatRate(campaign.replyRate)}</td>
									<td className={classNames(CELL, "text-status-success")}>
										{formatRate(campaign.positiveRate)}
									</td>
									<td className={CELL}>{formatRate(campaign.sentimentRatio)}</td>
								</tr>
							))}

							{data.campaigns.length === 0 ? (
								<tr>
									<td className="px-4 py-6 text-center text-sm text-text-meta" colSpan={6}>
										{t("analytics.top.noneRanked", { count: data.minContactedLeads })}
									</td>
								</tr>
							) : null}
						</tbody>
					</table>
				</div>
			</div>

			<p className="text-xs text-text-meta mt-3">
				{t("analytics.top.footnote", { count: data.minContactedLeads })}
			</p>
		</div>
	);
};
